import { useEffect, useRef, useState } from 'react';

/**
 * Hook para detectar atividade de voz com histerese simples (tempo mínimo no novo estado)
 * @param {MediaStream | null} stream
 * @param {{ smoothMs?: number, silenceTh?: number, speakingTh?: number }} [opts]
 *   smoothMs: tempo mínimo (ms) para confirmar troca de estado (default 350)
 *   silenceTh: limiar de silêncio (default 0.01)
 *   speakingTh: limiar de fala (default 0.15)
 * @returns {{ state: 'silent'|'noise'|'speaking', duration: number, rms: number }}
 */
export default function useVoiceActivity(stream, opts = {}) {
  const {
    smoothMs = 350,
    silenceTh = 0.01,
    speakingTh = 0.15
  } = opts;

  const [voice, setVoice] = useState({
    state: 'silent',
    duration: 0,
    rms: 0
  });

  // Estado confirmado (estável)
  const stableStateRef = useRef('silent');
  const stateSinceRef = useRef(performance.now());

  // Estado candidato (ainda não confirmado)
  const pendingStateRef = useRef(null);
  const pendingSinceRef = useRef(0);

  const rafRef = useRef(0);

  useEffect(() => {
    if (!stream) return;

    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const source = audioContext.createMediaStreamSource(stream);
    const analyser = audioContext.createAnalyser();
    analyser.fftSize = 256;
    source.connect(analyser);

    const dataArray = new Uint8Array(analyser.frequencyBinCount);

    const loop = () => {
      analyser.getByteFrequencyData(dataArray);

      const rms =
        Math.sqrt(
          dataArray.reduce((sum, v) => sum + v * v, 0) / dataArray.length
        ) / 255;

      // Mapear para estado bruto
      let rawState;
      if (rms < silenceTh) rawState = 'silent';
      else if (rms < speakingTh) rawState = 'noise';
      else rawState = 'speaking';

      const now = performance.now();

      // Se o bruto difere do estável, inicia/confirma transição
      if (rawState !== stableStateRef.current) {
        if (pendingStateRef.current !== rawState) {
          // novo candidato
          pendingStateRef.current = rawState;
          pendingSinceRef.current = now;
        } else
          // mesmo candidato continua — checa janela mínima
          if (now - pendingSinceRef.current >= smoothMs) {
            // confirma mudança
            stableStateRef.current = rawState;
            stateSinceRef.current = now;

            // limpamos pendência (opcional)
            pendingStateRef.current = null;
          }
      } else {
        // igual ao estável → nenhuma pendência
        pendingStateRef.current = null;
      }

      // Atualiza saída (estado estável + duração contínua)
      setVoice({
        state: stableStateRef.current,
        duration: now - stateSinceRef.current,
        rms
      });

      rafRef.current = requestAnimationFrame(loop);
    };

    // Alguns browsers requerem resume por política de áudio
    if (audioContext.state === 'suspended') {
      audioContext.resume().catch(() => {});
    }

    rafRef.current = requestAnimationFrame(loop);

    return () => {
      cancelAnimationFrame(rafRef.current);
      audioContext.close();
    };
  }, [stream, smoothMs, silenceTh, speakingTh]);

  return voice;
}
